import { useEffect, useRef, useState, type ChangeEvent } from "react";
import Modal from "@/components/base/Modal";
import { Field, Input } from "@/components/base/FormControls";
import { cn } from "@/utils/cn";
import { formatFcfa } from "@/utils/format";
import type { FormuleId, Periodicite } from "@/config/formules";
import {
  METHODES_PAIEMENT,
  PREUVE_ACCEPT,
  PREUVE_EXTENSIONS,
  getMethodePaiement,
  type PaiementMethode,
} from "@/config/paiement";
import { soumettrePaiementAbonnementRpc } from "@/store/platform/paiementAbonnement";
import { uploadPreuve, validatePreuveFile } from "@/store/shared/preuves";

interface PaiementAbonnementModalProps {
  open: boolean;
  onClose: () => void;
  formuleCode: FormuleId;
  formuleNom: string;
  periodicite: Periodicite;
  /** Montant officiel (calculé côté serveur à la soumission). */
  montant: number;
  /** Moyen de paiement pré-sélectionné (ex. choix fait à l'inscription). */
  initialMethode?: PaiementMethode | "";
  /** Référence de transaction pré-remplie (ex. saisie à l'inscription). */
  initialReference?: string;
  /**
   * Fonction de soumission serveur. Par défaut, la RPC d'abonnement de l'espace
   * client ; à l'inscription, on passe la RPC dédiée au compte en attente.
   */
  submitPaiement?: (params: {
    formuleCode: FormuleId;
    periodicite: Periodicite;
    methode: PaiementMethode;
    reference: string;
    preuveUrl: string;
  }) => Promise<unknown>;
  onSuccess: () => void;
}

/**
 * Demande de paiement d'abonnement (paiement local manuel vérifié par preuve).
 *
 * - Orange Money / Moov Money : numéro affiché + RÉFÉRENCE et PREUVE obligatoires.
 * - Espèces : aucune preuve requise.
 * Le montant n'est jamais saisi par le client : il est recalculé côté serveur
 * à partir de la formule et de la périodicité. Le paiement reste `en_attente`
 * jusqu'à la validation du Super Admin.
 */
export default function PaiementAbonnementModal({
  open,
  onClose,
  formuleCode,
  formuleNom,
  periodicite,
  montant,
  initialMethode = "",
  initialReference = "",
  submitPaiement,
  onSuccess,
}: PaiementAbonnementModalProps) {
  const [methode, setMethode] = useState<PaiementMethode | "">("");
  const [reference, setReference] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setMethode(initialMethode);
    setReference(initialReference);
    setFile(null);
    setFileError(null);
    setError(null);
    setSubmitting(false);
    setDone(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const info = methode ? getMethodePaiement(methode) : null;
  const preuveRequise = info?.preuveObligatoire ?? false;
  const canSubmit = methode !== "" && (!preuveRequise || (reference.trim().length > 0 && file !== null));

  const handleFile = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0];
    event.target.value = "";
    if (!selected) return;
    const err = validatePreuveFile(selected);
    if (err) {
      setFileError(err);
      setFile(null);
      return;
    }
    setFileError(null);
    setFile(selected);
  };

  const handleSubmit = async () => {
    if (!methode || !canSubmit || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      let preuvePath = "";
      if (preuveRequise && file) {
        preuvePath = await uploadPreuve(file);
      }
      const submit = submitPaiement ?? soumettrePaiementAbonnementRpc;
      await submit({
        formuleCode,
        periodicite,
        methode,
        reference: preuveRequise ? reference.trim() : "",
        preuveUrl: preuvePath,
      });
      setDone(true);
      onSuccess();
    } catch (caught) {
      setError(
        caught instanceof Error && caught.message
          ? caught.message
          : "La soumission du paiement a échoué. Réessayez."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Paiement de l'abonnement"
      description="Choisissez votre moyen de paiement. Le paiement est vérifié par notre équipe avant activation."
      footer={
        done ? (
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            Fermer
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer"
            >
              {submitting ? (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-background-50/40 border-t-background-50" />
              ) : (
                <i className="ri-send-plane-line text-base" aria-hidden="true" />
              )}
              {submitting ? "Envoi..." : "Soumettre le paiement"}
            </button>
          </>
        )
      }
    >
      {done ? (
        <div className="flex flex-col items-center gap-3 px-2 py-8 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
            <i className="ri-checkbox-circle-line text-3xl" aria-hidden="true" />
          </span>
          <h3 className="font-heading text-base font-semibold text-foreground-950">Demande de paiement envoyée</h3>
          <p className="max-w-md text-sm leading-relaxed text-foreground-600">
            Votre demande est <strong className="font-semibold text-foreground-900">en attente de validation</strong>.
            Dès que notre équipe aura vérifié le paiement, votre formule sera activée automatiquement.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-background-200 bg-background-100 px-4 py-3">
            <div>
              <p className="text-xs text-foreground-500">Formule &amp; période</p>
              <p className="text-sm font-label font-medium text-foreground-950">
                {formuleNom} · {periodicite === "annuel" ? "Annuelle" : "Mensuelle"}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-foreground-500">Montant à payer</p>
              <p className="font-heading text-lg font-semibold text-foreground-950">{formatFcfa(montant)}</p>
            </div>
          </div>

          <div className="flex flex-col gap-2.5">
            <span className="text-sm font-label font-medium text-foreground-800">Moyen de paiement</span>
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
              {METHODES_PAIEMENT.map((item) => {
                const active = methode === item.value;
                return (
                  <button
                    key={item.value}
                    type="button"
                    onClick={() => setMethode(item.value)}
                    className={cn(
                      "flex flex-col items-start gap-1.5 rounded-md border px-3.5 py-3 text-left transition-colors cursor-pointer",
                      active
                        ? "border-primary-400 bg-primary-50/60"
                        : "border-background-300 bg-background-50 hover:border-primary-300"
                    )}
                  >
                    <span className="flex items-center gap-2">
                      <i className={cn(item.icon, "text-base text-primary-600")} aria-hidden="true" />
                      <span className="text-sm font-label font-medium text-foreground-950">{item.label}</span>
                    </span>
                    {item.numero ? (
                      <span className="text-sm font-heading font-semibold tracking-wide text-foreground-900">
                        {item.numero}
                      </span>
                    ) : (
                      <span className="text-xs text-foreground-500">Paiement en espèces</span>
                    )}
                    <span
                      className={cn(
                        "text-[11px]",
                        item.preuveObligatoire ? "text-accent-700" : "text-foreground-500"
                      )}
                    >
                      {item.preuveObligatoire ? "Preuve obligatoire" : "Aucune preuve requise"}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {info && (
            <div className="flex flex-col gap-4">
              <p className="flex items-start gap-2 rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-xs leading-relaxed text-foreground-600">
                <i className="ri-information-line mt-0.5 text-sm text-foreground-400" aria-hidden="true" />
                {info.instruction}
              </p>

              {info.numero && (
                <div className="flex items-center justify-between gap-3 rounded-md border border-primary-200 bg-primary-50/60 px-4 py-3">
                  <span className="flex items-center gap-2 text-sm font-label font-medium text-primary-900">
                    <i className="ri-smartphone-line text-base" aria-hidden="true" />
                    Numéro {info.label}
                  </span>
                  <span className="font-heading text-lg font-semibold tracking-wide text-primary-900">
                    {info.numero}
                  </span>
                </div>
              )}

              {preuveRequise ? (
                <>
                  <Field
                    label="Référence de transaction"
                    htmlFor="pay-ref"
                    required
                    hint="Ex. l'identifiant de la transaction reçu par SMS."
                  >
                    <Input
                      id="pay-ref"
                      value={reference}
                      onChange={(event) => setReference(event.target.value)}
                      placeholder="Référence Orange / Moov Money"
                      maxLength={60}
                    />
                  </Field>

                  <div className="flex flex-col gap-1.5">
                    <span className="text-sm font-label font-medium text-foreground-800">
                      Preuve de paiement <span className="text-primary-600">*</span>
                    </span>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                      <button
                        type="button"
                        onClick={() => inputRef.current?.click()}
                        className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-3.5 py-2 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
                      >
                        <i className="ri-upload-2-line text-base" aria-hidden="true" />
                        {file ? "Changer le fichier" : "Joindre la preuve"}
                      </button>
                      {file && (
                        <span className="flex min-w-0 items-center gap-2 text-xs text-foreground-600">
                          <i className="ri-attachment-2 text-sm text-secondary-600" aria-hidden="true" />
                          <span className="truncate">{file.name}</span>
                          <button
                            type="button"
                            onClick={() => setFile(null)}
                            aria-label="Retirer la preuve"
                            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 cursor-pointer"
                          >
                            <i className="ri-close-line text-sm" aria-hidden="true" />
                          </button>
                        </span>
                      )}
                    </div>
                    <input
                      ref={inputRef}
                      type="file"
                      accept={PREUVE_ACCEPT}
                      className="sr-only"
                      onChange={handleFile}
                    />
                    <p className="text-xs text-foreground-500">
                      Formats acceptés : {PREUVE_EXTENSIONS}. 5 Mo maximum.
                    </p>
                    {fileError && (
                      <p className="text-xs text-primary-700" role="alert">
                        {fileError}
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <p className="flex items-start gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-3.5 py-2.5 text-xs leading-relaxed text-secondary-900">
                  <i className="ri-shield-check-line mt-0.5 text-sm" aria-hidden="true" />
                  Aucune preuve n'est demandée pour un paiement en espèces. Votre demande restera en attente
                  jusqu'à validation par un agent.
                </p>
              )}
            </div>
          )}

          {error && (
            <p className="flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50/60 px-3.5 py-2.5 text-xs text-primary-800" role="alert">
              <i className="ri-error-warning-line mt-0.5 text-sm" aria-hidden="true" />
              {error}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}